// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { addToEndOfChain, Handler } from "@insite/client-framework/HandlerCreator";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    AddToWishListParameter,
    chain as AddToWishList,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";

interface Props {
    result: {
        wishList?: WishListModel;
        errorMessage?: string;
    };
}

type HandlerType = Handler<AddToWishListParameter, Props>;

export const XngGaAddToWishlist: HandlerType = async props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const currentBillTo = getCurrentBillToState(state).value;
    const session = getSession(state);
    const locationId = session?.pickUpWarehouse?.id;

    const { productInfos } = props.parameter;
    const { wishList } = props.result;

    const getProduct = await getProductById({
        id: productInfos?.[0]?.productId,
    });

    if (state.xGoogleAnalytics.includeAddToWishlistEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: GOOGLE_EVENTS.ADD_TO_WISHLIST,
            ecommerce: {
                currency: state.context.session.currency?.currencyCode,
                value: productInfos?.[0]?.pricing?.unitNetPrice,
                items: [
                    {
                        item_id: getProduct?.productNumber,
                        item_name: getProduct?.productTitle,
                        affiliation: currentBillTo?.companyName,
                        index: 0,
                        item_brand: getProduct?.brand?.name,
                        item_list_id: wishList?.id,
                        item_list_name: wishList?.name,
                        location_id: locationId,
                        price: productInfos?.[0]?.pricing?.unitNetPrice,
                        quantity: productInfos?.[0]?.qtyOrdered,
                        ...parseProductCategories(getProduct?.properties?.categories, false),
                    },
                ],
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

const _GaAddToWishlist = XngGaAddToWishlist as any;
addToEndOfChain(AddToWishList, _GaAddToWishlist);
